import Cookies from 'js-cookie';

// Type pour stocker les informations de géolocalisation
export interface GeoLocationInfo {
  country: string;
  countryCode: string;
  language: string;
}

// Constantes pour les cookies et la durée de validité (30 jours)
const GEO_COOKIE_NAME = 'user_geo_location';
const COOKIE_EXPIRY_DAYS = 30;

// Liste des pays francophones qui auront le français comme langue par défaut
const FRENCH_SPEAKING_COUNTRIES = [
  'FR', // France
  'CA', // Canada (partiellement francophone)
  'BE', // Belgique
  'CH', // Suisse
  'LU', // Luxembourg
  'MC', // Monaco
  'SN', // Sénégal
  'CI', // Côte d'Ivoire
  'CD', // République démocratique du Congo
  'CM', // Cameroun
  'MG', // Madagascar
  'ML', // Mali
  'NE', // Niger
  'BF', // Burkina Faso
  'BJ', // Bénin
  'TG', // Togo
  'GA', // Gabon
  'GN', // Guinée
  'HT', // Haïti
  'MU', // Maurice
  'SC', // Seychelles
  'DJ', // Djibouti
  'RW', // Rwanda
  'BI', // Burundi
  'KM', // Comores
  'TD', // Tchad
  'CF', // République centrafricaine
  'CG', // République du Congo
  'GF', // Guyane française
  'GP', // Guadeloupe
  'MQ', // Martinique
  'RE', // Réunion
  'YT', // Mayotte
  'NC', // Nouvelle-Calédonie
  'PF', // Polynésie française
  'WF', // Wallis-et-Futuna
  'PM', // Saint-Pierre-et-Miquelon
  'BL', // Saint-Barthélemy
  'MF', // Saint-Martin
];

// Liste des pays germanophones qui auront l'allemand comme langue par défaut
const GERMAN_SPEAKING_COUNTRIES = [
  'DE', // Allemagne
  'AT', // Autriche
  'LI', // Liechtenstein
  'CH', // Suisse (partiellement germanophone)
];

/**
 * Détermine la langue par défaut en fonction du code pays
 * @param countryCode Code pays à deux lettres (ISO 3166-1 alpha-2)
 * @returns Code langue à utiliser par défaut
 */
export const getDefaultLanguageFromCountry = (countryCode: string): string => {
  if (FRENCH_SPEAKING_COUNTRIES.includes(countryCode)) {
    return 'fr';
  } else if (GERMAN_SPEAKING_COUNTRIES.includes(countryCode)) {
    return 'de';
  } else {
    return 'en'; // Anglais par défaut pour tous les autres pays
  }
};

/**
 * Récupère les informations de géolocalisation depuis le cookie s'il existe
 * @returns Informations de géolocalisation ou null si le cookie n'existe pas
 */
export const getGeoLocationFromCookie = (): GeoLocationInfo | null => {
  console.log("Vérification du cookie de géolocalisation...");
  const geoData = Cookies.get(GEO_COOKIE_NAME);
  console.log("Cookie de géolocalisation:", geoData);
  
  if (geoData) {
    try {
      const parsedData = JSON.parse(geoData);
      console.log("Données de géolocalisation du cookie:", parsedData);
      return parsedData;
    } catch (error) {
      console.error('Erreur lors de la lecture du cookie de géolocalisation:', error);
      return null;
    }
  }
  return null;
};

/**
 * Sauvegarde les informations de géolocalisation dans un cookie
 * @param geoInfo Informations de géolocalisation à sauvegarder
 */
export const saveGeoLocationToCookie = (geoInfo: GeoLocationInfo): void => {
  Cookies.set(GEO_COOKIE_NAME, JSON.stringify(geoInfo), {
    expires: COOKIE_EXPIRY_DAYS,
    sameSite: 'lax',
    path: '/'
  });
};

/**
 * Récupère la géolocalisation de l'utilisateur via une API gratuite
 * @returns Promise avec les informations de géolocalisation
 */
export const fetchUserGeoLocation = async (): Promise<GeoLocationInfo> => {
  console.log("Appel de l'API de géolocalisation...");
  try {
    // Utilisation de l'API ipapi.co qui ne nécessite pas de clé API pour l'usage de base
    const response = await fetch('https://ipapi.co/json/');
    
    if (!response.ok) {
      throw new Error(`Erreur lors de la requête: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("Réponse de l'API de géolocalisation:", data);
    
    const countryCode = data.country_code || 'US';
    const language = getDefaultLanguageFromCountry(countryCode);
    
    const geoInfo: GeoLocationInfo = {
      country: data.country_name || 'Unknown',
      countryCode,
      language
    };
    
    console.log("Informations de géolocalisation générées:", geoInfo);
    
    // Sauvegarde des informations dans un cookie
    saveGeoLocationToCookie(geoInfo);
    
    return geoInfo;
  } catch (error) {
    console.error('Erreur lors de la récupération de la géolocalisation:', error);
    
    // En cas d'erreur, on utilise des valeurs par défaut
    const defaultGeoInfo: GeoLocationInfo = {
      country: 'Unknown',
      countryCode: 'US',
      language: 'en'
    };
    
    console.log("Utilisation des valeurs par défaut:", defaultGeoInfo);
    saveGeoLocationToCookie(defaultGeoInfo);
    
    return defaultGeoInfo;
  }
};

/**
 * Récupère les informations de géolocalisation et la langue par défaut
 * Vérifie d'abord le cookie, puis fait une requête API si nécessaire
 * @returns Promise avec les informations de géolocalisation
 */
export const getUserGeoLocation = async (): Promise<GeoLocationInfo> => {
  console.log("Récupération des informations de géolocalisation...");
  
  // Vérifier si les informations sont déjà en cache dans un cookie
  const cachedGeoInfo = getGeoLocationFromCookie();
  
  if (cachedGeoInfo) {
    console.log("Utilisation des informations en cache:", cachedGeoInfo);
    return cachedGeoInfo;
  }
  
  console.log("Aucune information en cache, appel de l'API...");
  // Si pas de cache, faire une requête à l'API
  return fetchUserGeoLocation();
}; 